import React, { useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { CameraAlt, QrCode, Search } from '@mui/icons-material';

// ─── Bootstrap 3 style constants ─────────────────────────────────────────────
const bs3 = {
  panelDefault: {
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxShadow: 'none',
    mb: 2,
    bgcolor: '#fff',
  },
  panelHeading: (color = '#f5f5f5', textColor = '#333') => ({
    bgcolor: color,
    color: textColor,
    px: 2,
    py: 1.2,
    borderBottom: '1px solid #ddd',
    borderRadius: '4px 4px 0 0',
    fontWeight: 700,
    fontSize: '14px',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  }),
  panelBody: { p: 2 },
  btnPrimary: {
    bgcolor: '#337ab7',
    color: '#fff',
    border: '1px solid #2e6da4',
    borderRadius: '4px',
    px: 2,
    py: 0.8,
    fontSize: '13px',
    fontWeight: 400,
    textTransform: 'none',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    '&:hover': { bgcolor: '#286090', borderColor: '#204d74' },
    '&:disabled': { bgcolor: '#337ab7', opacity: 0.65 },
  },
  btnDefault: {
    bgcolor: '#fff',
    color: '#333',
    border: '1px solid #ccc',
    borderRadius: '4px',
    px: 2,
    py: 0.8,
    fontSize: '13px',
    fontWeight: 400,
    textTransform: 'none',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    '&:hover': { bgcolor: '#e6e6e6', borderColor: '#adadad' },
  },
  btnInfo: {
    bgcolor: '#5bc0de',
    color: '#fff',
    border: '1px solid #46b8da',
    borderRadius: '4px',
    px: 2,
    py: 0.8,
    fontSize: '13px',
    fontWeight: 400,
    textTransform: 'none',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    '&:hover': { bgcolor: '#31b0d5', borderColor: '#269abc' },
  },
  formControl: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      fontSize: '13px',
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      '& fieldset': { borderColor: '#ccc' },
      '&:hover fieldset': { borderColor: '#66afe9' },
      '&.Mui-focused fieldset': {
        borderColor: '#66afe9',
        boxShadow: 'inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px rgba(102,175,233,.6)',
      },
    },
  },
  badge: (bg = '#337ab7') => ({
    display: 'inline-block',
    bgcolor: bg,
    color: '#fff',
    fontSize: '11px',
    fontWeight: 700,
    px: 1,
    py: 0.2,
    borderRadius: '10px',
    lineHeight: 1.4,
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  }),
  alertSuccess: {
    bgcolor: '#dff0d8',
    color: '#3c763d',
    border: '1px solid #d6e9c6',
    borderRadius: '4px',
    p: 1.5,
    fontSize: '13px',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
};

// ─── SearchBarRetail ──────────────────────────────────────────────────────────
/**
 * Props:
 *  - lookupCode        : string — nilai input barcode/kode
 *  - setLookupCode     : setter
 *  - lookupLoading     : boolean
 *  - lookupProduct     : object | null — hasil lookup terakhir (untuk info card)
 *  - onLookup          : (kodebarang: string) => void
 *  - cameraEnabled     : boolean
 *  - onToggleCamera    : () => void
 *  - cameraContent     : ReactNode
 *  - inputRef          : React.RefObject — untuk autofocus scanner hardware
 */
const SearchBarRetail = ({
  lookupCode,
  setLookupCode,
  lookupLoading,
  lookupProduct,
  onLookup,
  cameraEnabled = false,
  onToggleCamera,
  cameraContent = null,
  inputRef,
}) => {
  const focusScanner = () => {
    setTimeout(() => inputRef?.current?.focus(), 100);
  };

  return (
    <Box sx={bs3.panelDefault}>
      {/* ── Panel Header ── */}
      <Box sx={bs3.panelHeading('#337ab7', '#fff')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCode sx={{ fontSize: 18 }} />
          <span>Scan / Cari Barang</span>
        </Box>
        <Button
          size="small"
          startIcon={<CameraAlt sx={{ fontSize: 16 }} />}
          onClick={onToggleCamera}
          sx={{ ...bs3.btnInfo, py: 0.4, px: 1.5, fontSize: '11px', minWidth: 'auto' }}
        >
          {cameraEnabled ? 'Tutup Kamera' : 'Buka Kamera'}
        </Button>
      </Box>

      {/* ── Panel Body ── */}
      <Box sx={bs3.panelBody}>
        <Grid container spacing={1.5} alignItems="center">
          {/* Input barcode */}
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              placeholder="Scan barcode atau ketik kode barang..."
              value={lookupCode}
              inputRef={inputRef}
              onChange={(e) => setLookupCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onLookup(lookupCode);
                }
              }}
              size="small"
              sx={bs3.formControl}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <QrCode sx={{ fontSize: 18, color: '#999' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Tombol Cari + Fokus */}
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                startIcon={
                  lookupLoading
                    ? <CircularProgress size={14} color="inherit" />
                    : <Search sx={{ fontSize: 16 }} />
                }
                onClick={() => onLookup(lookupCode)}
                disabled={lookupLoading}
                sx={bs3.btnPrimary}
              >
                Cari
              </Button>
              <Button
                onClick={focusScanner}
                sx={{ ...bs3.btnDefault, minWidth: 'auto', px: 1.5 }}
                title="Fokus Scanner"
              >
                <QrCode sx={{ fontSize: 18 }} />
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* ── Lookup result info card ── */}
        {lookupProduct && (
          <Box
            sx={{
              ...bs3.alertSuccess,
              mt: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <strong>{lookupProduct.Barang?.namabarang}</strong>
              <span style={{ marginLeft: 8, fontSize: '12px', color: '#666' }}>
                ({lookupProduct.Barang?.kodebarang || '-'})
              </span>
              <span style={{ marginLeft: 12 }}>
                Rp {Number(lookupProduct.Barang?.harga || 0).toLocaleString('id-ID')}
              </span>
            </Box>
            <Box sx={bs3.badge(lookupProduct.stok > 0 ? '#5cb85c' : '#d9534f')}>
              Stok: {lookupProduct.stok || 0}
            </Box>
          </Box>
        )}

        {cameraEnabled && cameraContent}
      </Box>
    </Box>
  );
};

export default SearchBarRetail;